import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdminPageHeader,
  Button,
  ButtonSize,
  //  ButtonStyles,
  ButtonWidth,
  Drawer,
  DropDown,
  Label,
  MLPVersion,
  TableWithSearchTemplate,
} from '../../../ui/components';
import { LoggerService, dateTimeToCustomFormat } from '../../../ui/utils';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { useAPICall } from '../../../hooks';
import { DynamicObjectInterface } from '../../../sdk/type';
import CoachService from '../../../sdk/services/coachService';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import CampaignService from '../../../sdk/services/campaignService';
import { CoachData, MasterData, CampaignOption, dataSearchOption, getCoachListConfig } from './coachConfig';
import styles from './index.module.scss';

export const AdminGeneralCoachList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useAPICall();

  const [data, setData] = useState<CoachData[]>([]);
  const [masterData, setMasterData] = useState<MasterData | null>();
  const [campaignList, setCampaignList] = useState<CampaignOption[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption>();
  const [selectedRow, setSelectedRow] = useState<CoachData | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [displayEmpty, setDisplayEmpty] = useState(false);

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (masterData) {
      setCampaignList(
        masterData.campaign
          ?.map(
            c =>
              ({
                ...c,
                title: `${c.campaignId} - ${c.campaignName}`,
                value: c.campaignId ?? '',
              }) as CampaignOption,
          )
          .sort((a, b) => {
            if (a.campaignStart > b.campaignStart) return -1;
            if (a.campaignStart < b.campaignStart) return 1;
            return 0;
          }),
      );
    }
  }, [masterData]);

  useEffect(() => {
    if (campaignList?.length) {
      const params = new URLSearchParams(location.search);
      const campaignId = params.get('campaignId') ?? '';
      if (campaignId) {
        setSelectedCampaign(prev => {
          let _selectedCampaign = campaignList.find(c => c.campaignId === campaignId);
          if (_selectedCampaign) return { ..._selectedCampaign };
          else return prev;
        });
      } else {
        let latest: CampaignOption = campaignList.reduce((latest: DynamicObjectInterface, current) => {
          const currentStartDate = new Date(current.campaignStart);
          const latestStartDate = latest ? new Date(latest.campaignStart) : null;

          if (!latestStartDate || currentStartDate > latestStartDate) return { ...current, value: current.campaignId };
          else return latest;
        }, null);
        navigate(`?campaignId=${latest.value}`, { replace: true });
      }
    }
  }, [campaignList, location]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchCoachListByCampaignId();
    }
  }, [selectedCampaign]);

  const fetchCoachListByCampaignId = async () => {
    const result = await apiCall(CoachService.getCoachByCampaignId, selectedCampaign?.value!);
    if (result.status === 200) {
      setDisplayEmpty(result.data.data.length === 0);

      const _formattedData = result.data.data?.map((item: DynamicObjectInterface) => {
        item.totalPoint = parseFloat(item.totalPoint).toFixed(1);
        return { ...item };
      });
      setData(_formattedData ?? []);
    } else {
      setDisplayEmpty(true);
      LoggerService.log('Failed to retrieve coach list by campaign');
    }
  };

  const fetchMasterData = async () => {
    let list = {
      campaign: {
        endpoint: CampaignService.getCampaigns,
      },
    };
    try {
      const results = await Promise.all(Object.values(list)?.map(l => apiCall(l.endpoint)));

      setMasterData(
        Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
          result[key] = results[idx]?.data?.data?.filter(
            (campaign: DynamicObjectInterface) => campaign.campaignType === 'CHALLENGE'
          );
          return result;
        }, {}),
      );
    } catch (error) {
      LoggerService.log('Failed to retrieve coach list', error);
    }
  };

  const onEdit = (data: CoachData | undefined) => {
    let recordId = data?.recId ?? selectedRow?.recId;
    navigate(`/admin/challenge/manage-coach/edit?=${recordId}&campaignId=${selectedCampaign?.campaignId}`);
  };

  const handleInputChange = (value: DynamicObjectInterface) => {
    sessionStorage.removeItem('Pagination');
    navigate(`?campaignId=${value.campaignId}`);
  };

  // const buttonConfig = [
  //   {
  //     title: t('admin.generalCoach.addCoachEnrollment'),
  //     buttonStyle: ButtonStyles.SOLID,
  //     onClick: () => {
  //       navigate(`/admin/challenge/coach-enrollment-configuration/edit?=new`);
  //     },
  //   },
  // ];

  const drawerDetails = (
    <>
      {drawerOpen && (
        <Drawer
          onClose={() => setDrawerOpen(false)}
          closeOnBackdropClick={true}
          closeIcon={true}
          header={<div>Details</div>}
          drawerWidth={'490px'}
          bottomGapFooter="0px"
          footer={
            <div>
              <Button
                title="Edit"
                onClick={() => onEdit(undefined)}
                size={ButtonSize.SMALL}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
            </div>
          }
        >
          <div className={styles['summary-pane-body']}>
            <div className={styles['basic-details']}>
              <div className={styles['title-value']}>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.coachEnrollmentMaster.campaignId')} value={selectedRow?.campaignId} />
                  <Label title={t('admin.coachEnrollmentMaster.campaignName')} value={selectedRow?.campaignName} />
                </div>
              </div>
              <hr className={styles['horizontal-divider']} />
              <div className={styles['title-value']}>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.coachEnrollmentMaster.coachId')} value={selectedRow?.coachId!} />
                  <Label title={t('admin.coachEnrollmentMaster.coachAboId')} value={selectedRow?.aboId} />
                </div>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.coachEnrollmentMaster.coachName')} value={selectedRow?.name} />
                  <Label title={t('admin.coachEnrollmentMaster.coachPin')} value={selectedRow?.pin} />
                </div>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.generalCoach.totalTeams')} value={`${selectedRow?.totalTeams}`} />
                  <Label
                    title={t('admin.generalCoach.totalTeamAchieved')}
                    value={`${selectedRow?.totalTeamAchieved ? selectedRow?.totalTeamAchieved : 0}`}
                  />
                </div>
                {/* <Label title={t('admin.coachEnrollmentMaster.directLeaderPin')} value={selectedRow?.directLeaderPin} /> */}
              </div>
              <hr className={styles['horizontal-divider']} />
              {/* <div className={styles['title-value']}>
                <Label
                  title={t('admin.coachEnrollmentMaster.enrollmentDate')}
                  value={
                    selectedRow?.enrollmentDate
                      ? dateTimeToCustomFormat(selectedRow?.enrollmentDate?.toString(), 'MMM YYYY')
                      : ''
                  }
                />
                <Label
                  title={t('admin.coachEnrollmentMaster.period')}
                  value={
                    selectedRow?.enrollmentDate && selectedRow?.endDate
                      ? dateTimeToCustomFormat(selectedRow?.enrollmentDate?.toString(), 'MMM YYYY') +
                        ' to ' +
                        dateTimeToCustomFormat(selectedRow?.endDate?.toString(), 'MMM YYYY')
                      : ''
                  }
                />
              </div> */}
              <Label title={t('admin.coachEnrollmentMaster.remark')} value={selectedRow?.remark} />
            </div>
          </div>
        </Drawer>
      )}
    </>
  );

  return (
    <div>
      <AdminPageHeader title={t('admin.generalCoach.coachList')} />
      <div className={styles['coach-list-top']}>
        <DropDown
          name="campaignId"
          dropdownLabel={`${t('admin.teams.campaign')}`}
          disabledFilter={false}
          mlpVersion={MLPVersion.ONE}
          value={selectedCampaign?.value}
          data={campaignList as unknown as DropDownData[]}
          configData={{
            label: 'title',
            onSelect: (selectedOption: DropDownData) => {
              handleInputChange(selectedOption);
            },
          }}
          readOnly={false}
        />
        <div className={styles['coach-list-top-layout']}>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.period')}:</div>
            <p>
              {selectedCampaign?.campaignStart && selectedCampaign.campaignEnd
                ? `${dateTimeToCustomFormat(selectedCampaign.campaignStart, 'DD MMM YYYY')} - ${dateTimeToCustomFormat(
                    selectedCampaign.campaignEnd,
                    'DD MMM YYYY',
                  )}`
                : '-'}
            </p>
          </div>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.status')}:</div>
            <p>{selectedCampaign?.status ?? '-'}</p>
          </div>
        </div>
      </div>
      {displayEmpty ? (
        <div className={styles['no-results-container']}>
          <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
        </div>
      ) : (
        <TableWithSearchTemplate
          dataSource={data}
          searchByStrip
          getColumns={getCoachListConfig}
          data={dataSearchOption}
          hideAddButton
          rowKey="recId"
          selected={`${selectedRow?.recId}`}
          cb={{ onEdit }}
          onRowClick={row => {
            setSelectedRow(row);
            setDrawerOpen(true);
          }}
        />
      )}
      {drawerDetails}
    </div>
  );
};
